import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components/macro';
import { ReactComponent as Icon } from '../Assets/Icons/loading.svg';
import { Rotate } from './Animations';

const Container = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const ChildrenContainer = styled.div<{ $loading: boolean }>`
  transition: opacity 0.3s;
  position: relative;
  ${(props) => (props.$loading ? 'opacity: 0.5;' : '')}
`;

const Overlay = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 999;
`;

const StyledIcon = styled(Icon)`
  width: 100%;
  height: 100%;
  path {
    fill: ${(props) => props.color || props.theme.textColor};
  }
`;

export interface LoadingProps {
  children?: React.ReactNode;
  size?: number;
  color?: string;
  loading?: boolean;
  delay?: number;
  className?: string;
  _id?: string;
}

const Loading = (props: LoadingProps) => {
  const {
    children,
    size = 32,
    color,
    loading: isLoading = true,
    delay = 0,
    _id,
    ...restProps
  } = props;

  const [loading, setLoading] = useState(isLoading);
  const timer = useRef<NodeJS.Timeout | undefined>(undefined);

  // Delay showing the spinner based on passes `delay` prop
  useEffect(() => {
    if (isLoading) timer.current = setTimeout(() => setLoading(true), delay);

    if (!isLoading) {
      clearTimeout(timer.current);
      setLoading(false);
    }
  }, [isLoading, delay]);

  let rotateProps = {
    width: `${size}px`,
    height: `${size}px`,
  };

  // allow restProps to be applied
  // to the outermost container
  // even with no children
  if (!children) {
    rotateProps = {
      ...rotateProps,
      ...restProps,
    };
  }

  const iconProps = {
    alt: 'loading',
    color,
  };

  const spinner = (
    <Rotate {...rotateProps}>
      <StyledIcon {...iconProps} />
    </Rotate>
  );

  const childrenContainerProps = {
    $loading: loading,
    id: _id,
    className: 'scroll-m-[calc(125px_+_2.25rem)] md:scroll-m-[calc(175px_+_2.25rem)]',
  };

  if (!children) {
    return spinner;
  } else {
    return (
      <Container {...restProps}>
        {loading ? <Overlay>{spinner}</Overlay> : null}
        <ChildrenContainer {...childrenContainerProps}>{children}</ChildrenContainer>
      </Container>
    );
  }
};

export default Loading;
